import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Badge,
  Table,
  Alert,
  Spinner,
  Button,
  Nav,
  Tab,
} from "react-bootstrap";
import { CSVLink } from "react-csv";
import { useAuth } from "../contexts/AuthContext";
import { fires } from "../firebase";
import { useNavigate, Link } from "react-router-dom";
import { PieChart } from "react-minimal-pie-chart";

export default function AdminDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("events");

  // Dashboard data states
  const [recentEvents, setRecentEvents] = useState([]);
  const [recentOrganizations, setRecentOrganizations] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentDrafts, setRecentDrafts] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({});

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load recent events
      const eventsSnapshot = await fires
        .collection("events")
        .orderBy("timestamp", "desc")
        .limit(10)
        .get();
      
      const eventsData = [];
      for (const eventDoc of eventsSnapshot.docs) {
        const eventData = eventDoc.data();
        eventsData.push({
          id: eventDoc.id,
          ...eventData,
          createdAt: eventData.timestamp?.toDate?.() || new Date(),
        });
      }
      setRecentEvents(eventsData);

      // Load recent organizations (groups)
      const groupsSnapshot = await fires
        .collection("groups")
        .orderBy("timestamp", "desc")
        .limit(10)
        .get();
      
      const groupsData = [];
      for (const groupDoc of groupsSnapshot.docs) {
        const groupData = groupDoc.data();
        groupsData.push({
          id: groupDoc.id,
          ...groupData,
          createdAt: groupData.timestamp?.toDate?.() || new Date(),
          memberCount: groupData.member_count || 0,
          eventCount: groupData.event_count || 0,
        });
      }
      setRecentOrganizations(groupsData);

      // Load recent users
      const usersSnapshot = await fires
        .collection("users")
        .orderBy("timestamp", "desc")
        .limit(10)
        .get();
      
      const usersData = [];
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        usersData.push({
          id: userDoc.id,
          ...userData,
          createdAt: userData.createdAt?.toDate?.() || new Date(),
          lastLogin: userData.lastLogin?.toDate?.() || null,
        });
      }
      setRecentUsers(usersData);

      // Load recent orders
      const ordersSnapshot = await fires
        .collection("orders")
        .orderBy("timestamp", "desc")
        .limit(10)
        .get();
      
      const ordersData = [];
      for (const orderDoc of ordersSnapshot.docs) {
        const orderData = orderDoc.data();
        ordersData.push({
          id: orderDoc.id,
          ...orderData,
          createdAt: orderData.timestamp?.toDate?.() || new Date(),
        });
      }
      setRecentOrders(ordersData);

      // Load recent drafts
      const draftsSnapshot = await fires
        .collection("drafts")
        .orderBy("timestamp", "desc")
        .limit(10)
        .get();
      
      const draftsData = [];
      for (const draftDoc of draftsSnapshot.docs) {
        const draftData = draftDoc.data();
        draftsData.push({
          id: draftDoc.id,
          ...draftData,
          createdAt: draftData.timestamp?.toDate?.() || new Date(),
        });
      }
      setRecentDrafts(draftsData);

      // Calculate dashboard statistics
      const stats = {
        totalEvents: eventsData.length,
        totalOrganizations: groupsData.length,
        totalUsers: usersData.length,
        totalOrders: ordersData.length,
        totalDrafts: draftsData.length,
        totalRevenue: ordersData.reduce((sum, order) => sum + (order.amount || 0), 0),
        completedOrders: ordersData.filter(order => order.status === "completed").length,
        pendingOrders: ordersData.filter(order => order.status === "pending").length,
        activeEvents: eventsData.filter(event => event.status === "live").length,
        draftEvents: draftsData.length,
      };
      setDashboardStats(stats);

    } catch (err) {
      setError("Failed to load dashboard data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      live: "success",
      draft: "secondary",
      cancelled: "danger",
      completed: "success",
      pending: "warning",
      processing: "info",
    };
    return <Badge bg={variants[status] || "secondary"}>{status || "unknown"}</Badge>;
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: "danger",
      superadmin: "dark",
      user: "primary",
      organizer: "info",
      "org admin": "warning",
    };
    return <Badge bg={variants[role] || "secondary"}>{role || "user"}</Badge>;
  };

  const exportDashboardData = () => {
    return {
      events: recentEvents.map(event => ({
        "Event ID": event.id,
        "Title": event.title || "N/A",
        "Status": event.status || "N/A",
        "Created": event.createdAt?.toLocaleString() || "N/A",
        "Start Date": event.startDate?.toDate?.()?.toLocaleString() || "N/A",
      })),
      organizations: recentOrganizations.map(org => ({
        "Org ID": org.id,
        "Name": org.name || "N/A",
        "Type": org.type || "N/A",
        "Created": org.createdAt?.toLocaleString() || "N/A",
      })),
              users: recentUsers.map(user => ({
          "User ID": user.id,
          "Email": user.email || "N/A",
          "Name": `${user.firstName || ""} ${user.lastName || ""}`,
          "Role": user.orgId ? "org admin" : (user.role || "N/A"),
          "Created": user.createdAt?.toLocaleString() || "N/A",
        })),
      orders: recentOrders.map(order => ({
        "Order ID": order.uniqueId || "N/A",
        "Event": order.title || "N/A",
        "Amount": order.amount || 0,
        "Status": order.status || "N/A",
        "User Email": order.userEmail || "N/A",
        "Created": order.createdAt?.toLocaleString() || "N/A",
      })),
    };
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          {error}
          <Button variant="outline-danger" className="ms-3" onClick={loadDashboardData}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Card className="mb-4" style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        border: "none",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
      }}>
        <Card.Body className="text-white">
          <Row className="align-items-center">
            <Col>
              <h2 className="mb-0">
                <i className="fas fa-tachometer-alt me-2"></i>
                Admin Dashboard
              </h2>
              <p className="mb-0 opacity-75">Overview of platform activity and statistics</p>
            </Col>
            <Col xs="auto">
              <Button
                variant="light"
                onClick={loadDashboardData}
                className="me-2"
              >
                <i className="fas fa-sync-alt me-1"></i>
                Refresh
              </Button>
              <CSVLink
                data={exportDashboardData().events}
                filename={`admin-dashboard-${new Date().toISOString().split('T')[0]}.csv`}
                className="btn btn-light"
              >
                <i className="fas fa-download me-1"></i>
                Export Data
              </CSVLink>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Quick Navigation */}
      <Row className="mb-4">
        <Col>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <h6 className="mb-3">Quick Navigation</h6>
              <div className="d-flex flex-wrap gap-2">
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => navigate('/user-management')}
                >
                  <i className="fas fa-users me-1"></i>
                  User Management
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => navigate('/order-management')}
                >
                  <i className="fas fa-shopping-cart me-1"></i>
                  Order Management
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => navigate('/admin-page-view-map')}
                >
                  <i className="fas fa-map-marker-alt me-1"></i>
                  Page View Map
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => navigate('/admin-email-sender')}
                >
                  <i className="fas fa-envelope me-1"></i>
                  Email Sender
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => navigate('/admin-email-campaign')}
                >
                  <i className="fas fa-bullhorn me-1"></i>
                  Email Campaign
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => navigate('/admin-cold-email')}
                >
                  <i className="fas fa-paper-plane me-1"></i>
                  Cold Email
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => navigate('/admin-weekly-email-campaign')}
                >
                  <i className="fas fa-calendar-week me-1"></i>
                  Weekly Email Campaign
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => navigate('/admin-page-views')}
                >
                  <i className="fas fa-chart-line me-1"></i>
                  Page Views
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => navigate('/analytics')}
                >
                  <i className="fas fa-chart-bar me-1"></i>
                  Analytics
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => navigate('/admin-all-payouts')}
                >
                  <i className="fas fa-money-bill-wave me-1"></i>
                  All Payouts
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Latest Information Cards */}
      <Row className="mb-4">
        <Col md={6}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Latest Users</h6>
                <Link to="/user-management" className="btn btn-sm btn-outline-primary">
                  View All
                </Link>
              </div>
              {recentUsers.length > 0 ? (
                <div>
                  {recentUsers.slice(0, 10).map((user) => (
                    <div key={user.id} className="d-flex align-items-center mb-2 p-2 border rounded">
                      <div className="me-3">
                        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                             style={{ width: "40px", height: "40px", fontSize: "14px" }}>
                          {(user.firstName?.charAt(0) || user.email?.charAt(0) || "U").toUpperCase()}
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-bold">{(user.firstName || "") + " " + (user.lastName || "")}</div>
                        <div className="text-muted small">{user.email}</div>
                        <div>{user.orgId ? getRoleBadge("org admin") : getRoleBadge(user.role)}</div>
                      </div>
                      <div className="text-end">
                        <div className="text-muted small">
                          {user.createdAt?.toLocaleDateString() || "N/A"}
                        </div>
                        <Link to={`/user/${user.id}`} className="btn btn-sm btn-outline-primary">
                          View
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="info">No users found.</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Latest Events</h6>
                <Link to="/all-events" className="btn btn-sm btn-outline-primary">
                  View All
                </Link>
              </div>
              {recentEvents.length > 0 ? (
                <div>
                  {recentEvents.slice(0, 10).map((event) => (
                    <div key={event.id} className="d-flex align-items-center mb-2 p-2 border rounded">
                      <div className="me-3">
                        <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" 
                             style={{ width: "40px", height: "40px", fontSize: "14px" }}>
                          <i className="fas fa-calendar"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-bold">{event.title || "Untitled Event"}</div>
                        <div className="text-muted small">
                          <i className="fas fa-map-marker-alt me-1"></i>
                          {event.address?.fullAddress || "No address"}
                        </div>
                        <div className="text-muted small">
                          <i className="fas fa-calendar me-1"></i>
                          {event.startDateString || "No start date"} - {event.endDateString || "No end date"}
                        </div>
                        <div>{getStatusBadge(event.status)}</div>
                      </div>
                      <div className="text-end">
                        <div className="text-muted small">
                          {event.createdAt?.toLocaleDateString() || "N/A"}
                        </div>
                        <Link to={`/event/${event.id}`} className="btn btn-sm btn-outline-primary">
                          View
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="info">No events found.</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Latest Organizations</h6>
                <Link to="/groups" className="btn btn-sm btn-outline-primary">
                  View All
                </Link>
              </div>
              {recentOrganizations.length > 0 ? (
                <div>
                  {recentOrganizations.slice(0, 10).map((org) => (
                    <div key={org.id} className="d-flex align-items-center mb-2 p-2 border rounded">
                      <div className="me-3">
                        <div className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center" 
                             style={{ width: "40px", height: "40px", fontSize: "14px" }}>
                          <i className="fas fa-building"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-bold">{org.name || "Untitled Organization"}</div>
                        <div className="text-muted small">{org.type || "No type"}</div>
                        <div className="small">
                          <span className="text-info">{org.memberCount || 0} members</span>
                          <span className="mx-2">•</span>
                          <span className="text-success">{org.eventCount || 0} events</span>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="text-muted small">
                          {org.createdAt?.toLocaleDateString() || "N/A"}
                        </div>
                        <Link to={`/org/${org.id}`} className="btn btn-sm btn-outline-primary">
                          View
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="info">No organizations found.</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Latest Orders</h6>
                <Link to="/order-management" className="btn btn-sm btn-outline-primary">
                  View All
                </Link>
              </div>
              {recentOrders.length > 0 ? (
                <div>
                  {recentOrders.slice(0, 10).map((order) => (
                    <div key={order.id} className="d-flex align-items-center mb-2 p-2 border rounded">
                      <div className="me-3">
                        <div className="bg-info text-white rounded-circle d-flex align-items-center justify-content-center" 
                             style={{ width: "40px", height: "40px", fontSize: "14px" }}>
                          <i className="fas fa-shopping-cart"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-bold">{order.title || "Unknown Event"}</div>
                        <div className="text-muted small">{order.userEmail || "No email"}</div>
                        <div className="text-success fw-bold">${order.amount || 0}</div>
                      </div>
                      <div className="text-end">
                        <div className="text-muted small">
                          {order.createdAt?.toLocaleDateString() || "N/A"}
                        </div>
                        <div className="mb-1">{getStatusBadge(order.status)}</div>
                        <Link to={`/order/${order.uniqueId}`} className="btn btn-sm btn-outline-primary">
                          View
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="info">No orders found.</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Latest Drafts</h6>
                <Link to="/admin-tourism-event-creator" className="btn btn-sm btn-outline-primary">
                  Create New
                </Link>
              </div>
              {recentDrafts.length > 0 ? (
                <div>
                  {recentDrafts.slice(0, 10).map((draft) => (
                    <div key={draft.id} className="d-flex align-items-center mb-2 p-2 border rounded">
                      <div className="me-3">
                        {draft.eventDetails?.coverImage ? (
                          <img 
                            src={draft.eventDetails.coverImage} 
                            alt="Cover"
                            style={{ 
                              width: "60px", 
                              height: "60px", 
                              objectFit: "cover",
                              borderRadius: "8px"
                            }}
                          />
                        ) : (
                          <div className="bg-warning text-white rounded-circle d-flex align-items-center justify-content-center" 
                               style={{ width: "60px", height: "60px", fontSize: "16px" }}>
                            <i className="fas fa-image"></i>
                          </div>
                        )}
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-bold">{draft.eventDetails?.title || "Untitled Draft"}</div>
                        <div className="text-muted small">
                          {draft.eventDetails?.highlight || "No description"}
                        </div>
                        <div className="text-muted small">
                          <i className="fas fa-calendar me-1"></i>
                          {draft.eventDetails?.date?.start ? 
                            `${draft.eventDetails.date.start} - ${draft.eventDetails.date.end || draft.eventDetails.date.start}` : 
                            "No date set"
                          }
                        </div>
                        <div>{getStatusBadge(draft.status)}</div>
                      </div>
                      <div className="text-end">
                        <div className="text-muted small">
                          {draft.createdAt?.toLocaleDateString() || "N/A"}
                        </div>
                        <Button 
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/admin-tourism-event-creator?draftId=${draft.id}`)}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="info">No drafts found.</Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Analytics Chart */}
      <Row className="mb-4">
        <Col md={6}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <h6 className="mb-3">Platform Activity Overview</h6>
              <div style={{ height: "200px" }}>
                <PieChart
                  data={[
                    { title: 'Events', value: dashboardStats.totalEvents, color: '#667eea' },
                    { title: 'Organizations', value: dashboardStats.totalOrganizations, color: '#11998e' },
                    { title: 'Users', value: dashboardStats.totalUsers, color: '#f093fb' },
                    { title: 'Orders', value: dashboardStats.totalOrders, color: '#4facfe' },
                    { title: 'Drafts', value: dashboardStats.totalDrafts, color: '#fa709a' },
                  ]}
                  label={({ dataEntry }) => `${dataEntry.title}: ${dataEntry.value}`}
                  labelStyle={{
                    fontSize: '5px',
                    fontFamily: 'sans-serif',
                  }}
                  labelPosition={50}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card style={{
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            borderRadius: "12px"
          }}>
            <Card.Body>
              <h6 className="mb-3">Order Status Distribution</h6>
              <div style={{ height: "200px" }}>
                <PieChart
                  data={[
                    { title: 'Completed', value: dashboardStats.completedOrders, color: '#11998e' },
                    { title: 'Pending', value: dashboardStats.pendingOrders, color: '#f093fb' },
                    { title: 'Processing', value: dashboardStats.totalOrders - dashboardStats.completedOrders - dashboardStats.pendingOrders, color: '#4facfe' },
                  ]}
                  label={({ dataEntry }) => `${dataEntry.title}: ${dataEntry.value}`}
                  labelStyle={{
                    fontSize: '5px',
                    fontFamily: 'sans-serif',
                  }}
                  labelPosition={50}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Card style={{
        border: "none",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        borderRadius: "12px"
      }}>
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
            <Row>
              <Col md={3}>
                <Nav variant="pills" className="flex-column">
                  <Nav.Item>
                    <Nav.Link eventKey="events">Recent Events</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="organizations">Organizations</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="users">Recent Users</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="orders">Recent Orders</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="drafts">Event Drafts</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col md={9}>
                <Tab.Content>
                  <Tab.Pane eventKey="events">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Recent Events ({recentEvents.length})</h6>
                      <CSVLink
                        data={exportDashboardData().events}
                        filename={`recent-events-${new Date().toISOString().split('T')[0]}.csv`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Export
                      </CSVLink>
                    </div>
                    {recentEvents.length > 0 ? (
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Event</th>
                            <th>Address</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentEvents.map((event) => (
                            <tr key={event.id}>
                              <td>{event.title || "N/A"}</td>
                              <td>{event.address?.fullAddress || "N/A"}</td>
                              <td>{event.startDateString || "N/A"}</td>
                              <td>{event.endDateString || "N/A"}</td>
                              <td>{getStatusBadge(event.status)}</td>
                              <td>{event.createdAt?.toLocaleDateString() || "N/A"}</td>
                              <td>
                                <Link 
                                  to={`/event/${event.id}`}
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  View
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">No events found.</Alert>
                    )}
                  </Tab.Pane>

                  <Tab.Pane eventKey="organizations">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Recent Organizations ({recentOrganizations.length})</h6>
                      <CSVLink
                        data={exportDashboardData().organizations}
                        filename={`recent-organizations-${new Date().toISOString().split('T')[0]}.csv`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Export
                      </CSVLink>
                    </div>
                    {recentOrganizations.length > 0 ? (
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Organization</th>
                            <th>Type</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentOrganizations.map((org) => (
                            <tr key={org.id}>
                              <td>{org.name || "N/A"}</td>
                              <td>{org.type || "N/A"}</td>
                              <td>{org.createdAt?.toLocaleDateString() || "N/A"}</td>
                              <td>
                                <Link 
                                  to={`/org/${org.id}`}
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  View
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">No organizations found.</Alert>
                    )}
                  </Tab.Pane>

                  <Tab.Pane eventKey="users">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Recent Users ({recentUsers.length})</h6>
                      <CSVLink
                        data={exportDashboardData().users}
                        filename={`recent-users-${new Date().toISOString().split('T')[0]}.csv`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Export
                      </CSVLink>
                    </div>
                    {recentUsers.length > 0 ? (
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentUsers.map((user) => (
                            <tr key={user.id}>
                              <td>{(user.firstName || "") + " " + (user.lastName || "")}</td>
                              <td>{user.email}</td>
                              <td>{user.orgId ? getRoleBadge("org admin") : getRoleBadge(user.role)}</td>
                              <td>{user.createdAt?.toLocaleDateString() || "N/A"}</td>
                              <td>
                                <Link 
                                  to={`/user/${user.id}`}
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  View
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">No users found.</Alert>
                    )}
                  </Tab.Pane>

                  <Tab.Pane eventKey="orders">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Recent Orders ({recentOrders.length})</h6>
                      <div>
                        <Link to="/order-management" className="btn btn-sm btn-outline-primary me-2">
                          View All
                        </Link>
                        <CSVLink
                          data={exportDashboardData().orders}
                          filename={`recent-orders-${new Date().toISOString().split('T')[0]}.csv`}
                          className="btn btn-sm btn-outline-primary"
                        >
                          Export
                        </CSVLink>
                      </div>
                    </div>
                    {recentOrders.length > 0 ? (
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Order ID</th>
                            <th>Event</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>User</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentOrders.map((order) => (
                            <tr key={order.id}>
                              <td>
                                <Link 
                                  to={`/order/${order.uniqueId}`}
                                  className="text-decoration-none"
                                >
                                  {order.uniqueId || "N/A"}
                                </Link>
                              </td>
                              <td>{order.title || "N/A"}</td>
                              <td>${order.amount || 0}</td>
                              <td>{getStatusBadge(order.status)}</td>
                              <td>{order.userEmail || "N/A"}</td>
                              <td>{order.createdAt?.toLocaleDateString() || "N/A"}</td>
                              <td>
                                <Link 
                                  to={`/order/${order.uniqueId}`}
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  View
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">No orders found.</Alert>
                    )}
                  </Tab.Pane>

                  <Tab.Pane eventKey="drafts">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Event Drafts ({recentDrafts.length})</h6>
                    </div>
                    {recentDrafts.length > 0 ? (
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Cover</th>
                            <th>Draft</th>
                            <th>Highlight</th>
                            <th>Date Range</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentDrafts.map((draft) => (
                            <tr key={draft.id}>
                              <td>
                                {draft.eventDetails?.coverImage ? (
                                  <img 
                                    src={draft.eventDetails.coverImage} 
                                    alt="Cover"
                                    style={{ 
                                      width: "40px", 
                                      height: "40px", 
                                      objectFit: "cover",
                                      borderRadius: "4px"
                                    }}
                                  />
                                ) : (
                                  <div className="bg-warning text-white rounded-circle d-flex align-items-center justify-content-center" 
                                       style={{ width: "40px", height: "40px", fontSize: "12px" }}>
                                    <i className="fas fa-image"></i>
                                  </div>
                                )}
                              </td>
                              <td>{draft.eventDetails?.title || "N/A"}</td>
                              <td>{draft.eventDetails?.highlight || "N/A"}</td>
                              <td>
                                {draft.eventDetails?.date?.start ? 
                                  `${draft.eventDetails.date.start} - ${draft.eventDetails.date.end || draft.eventDetails.date.start}` : 
                                  "N/A"
                                }
                              </td>
                              <td>{getStatusBadge(draft.status)}</td>
                              <td>{draft.createdAt?.toLocaleDateString() || "N/A"}</td>
                              <td>
                                <Button 
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => navigate(`/admin-tourism-event-creator?draftId=${draft.id}`)}
                                >
                                  Edit
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">No drafts found.</Alert>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </Card.Body>
      </Card>
    </Container>
  );
} 
